import { useEffect, useRef, useState, type ComponentRef } from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import type { MuxPlayerProps } from '@mux/mux-player-react';
import { useInView } from 'react-intersection-observer';

import { MediaProps } from '#/lib/sanity';
import { SanityVideoLoop } from '#/lib/sanity/queries/videoLoop';
import { cn, COLORS } from '#/lib/utils';

import { MediaCaption } from './Media';

const MuxPlayer = dynamic(() => import('@mux/mux-player-react'), {
  ssr: false,
});

type Props = {
  video: SanityVideoLoop;
  /** Takes precedence over the video.loop field */
  loop?: boolean;
} & MediaProps;

export function VideoLoop({
  video,
  className,
  mediaClassName,
  captionClassName,
  aspect = true,
  style,
  loop,
  sectionIndex,
}: Props) {
  const shouldPreloadVideo = sectionIndex != null ? sectionIndex <= 1 : false;
  const videoRef = useRef<ComponentRef<'video'>>(null);
  const resumeNativeRef = useRef(false);
  const isActiveRef = useRef(true);

  const [pageVisible, setPageVisible] = useState(true);
  // Controlled Mux: stay paused until the user presses play; remember that
  // intent across offscreen / background-tab forced pauses so we can resume.
  const [userPaused, setUserPaused] = useState(true);

  const { ref, inView } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    const syncPageVisibility = () => {
      setPageVisible(document.visibilityState === 'visible');
    };
    syncPageVisibility();
    document.addEventListener('visibilitychange', syncPageVisibility);
    return () => {
      document.removeEventListener('visibilitychange', syncPageVisibility);
    };
  }, []);

  // Allows for force-overriding the loop setting on a per-component basis
  let shouldLoop = video.loop;
  if (loop !== undefined) {
    shouldLoop = loop;
  }

  const playbackId = video.playbackId ?? undefined;
  const showControls = video.showControls;
  const poster = video.videoPosterUrl ?? undefined;

  // Pause when off-screen or the tab is hidden (ambient + controlled)
  const isActive = inView && pageVisible;
  isActiveRef.current = isActive;

  const shouldAutoPlay = !showControls && isActive;
  const muxPaused = showControls ? !isActive || userPaused : !shouldAutoPlay;

  useEffect(() => {
    if (playbackId) return;

    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (!isActive) {
      if (!videoEl.paused) {
        resumeNativeRef.current = Boolean(showControls);
        videoEl.pause();
      }
      return;
    }

    if (!showControls) {
      void videoEl.play()?.catch(() => undefined);
    } else if (resumeNativeRef.current) {
      resumeNativeRef.current = false;
      void videoEl.play()?.catch(() => undefined);
    }
  }, [isActive, playbackId, showControls]);

  return (
    <>
      {shouldPreloadVideo && video.url && !playbackId ? (
        <Head>
          <link
            rel="preload"
            as="video"
            href={video.url}
            type={video.mimeType ?? undefined}
          />
        </Head>
      ) : null}
      <figure
        ref={ref}
        data-section-index={sectionIndex}
        className={cn(
          'relative overflow-clip',
          video.roundedCorners && 'rounded-brand',
          className,
        )}
        style={{
          aspectRatio: aspect ? `${video.width} / ${video.height}` : undefined,
          ...style,
        }}
      >
        {playbackId ? (
          <MuxPlayer
            className={cn(
              'absolute inset-0 size-full transition-opacity duration-500',
              mediaClassName,
            )}
            style={
              {
                '--media-object-fit': 'cover',
                '--media-object-position': 'center',
                ...(showControls ? {} : { '--controls': 'none' }),
                width: '100%',
                height: '100%',
              } satisfies MuxPlayerProps['style']
            }
            playbackId={playbackId}
            poster={poster}
            streamType="on-demand"
            preload={shouldPreloadVideo ? 'auto' : 'metadata'}
            loop={shouldLoop}
            muted={!showControls}
            autoPlay={showControls ? false : 'muted'}
            paused={muxPaused}
            onPlay={showControls ? () => setUserPaused(false) : undefined}
            onPause={
              showControls
                ? () => {
                    // Ignore pauses we force while inactive so we can resume
                    if (isActiveRef.current) setUserPaused(true);
                  }
                : undefined
            }
            playsInline
            primaryColor={COLORS.cloud}
            secondaryColor={COLORS.charcoal}
            accentColor={COLORS.cipher.DEFAULT}
          />
        ) : video.url ? (
          <video
            poster={poster}
            ref={videoRef}
            className={cn(
              'absolute inset-0 size-full object-cover transition-opacity duration-500',
              mediaClassName,
            )}
            src={`${video.url}#t=0.1`}
            preload={shouldPreloadVideo ? 'auto' : 'metadata'}
            loop={shouldLoop}
            muted={!showControls}
            controls={showControls}
            playsInline
          />
        ) : null}
      </figure>
      {video.caption && (
        <MediaCaption
          caption={video.caption}
          className={captionClassName}
          textAlign={video.captionTextAlign}
        />
      )}
    </>
  );
}
